import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, CalendarDays, AlertTriangle, ExternalLink, Bell, Check, BarChart3, MessageCircle, Users, ChevronRight } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { KDoreseniSeznam } from '../layout/KDoreseni';
import Avatar from '../zamestnanci/Avatar';
import MojeKpi from '../../pages/prehled/MojeKpi';
import { smiNaStranku } from '../../lib/moduly';

/**
 * Cockpit → **Můj tým** — přehled pro vedoucího (2026-10-11). Záměrně
 * dashboard, ne norma seznam + detail: na první pohled má být vidět všechno.
 *
 * Bez hlavičky, přes celou plochu:
 *  - pruh čtyř čísel (ve službě · odpracováno · pozdní příchody · povinnosti)
 *  - jedna karta **Lidé** — řádek na člověka: dnešek na malé časové ose,
 *    hodiny proti plánu, pozdě, povinnosti jako tečky, výkon řidiče; nahoře
 *    přepínač období Dnes · Týden · Měsíc
 *  - vedle sloupec **Potřebuje tvou pozornost** s akcemi (Napsat do chatu,
 *    Připomenout, Otevřít)
 * Klik na člověka otevře jeho detail v okně (povinnosti, výkon, Lidé).
 *
 * Data: `/api/me/tym?obdobi=` (MujTymPrehled) a zvlášť `/api/me/tym/vykon`
 * (drahé — prochází objednávky). Na mobilu je to sekce Cockpitu, na desktopu
 * pohled za přepínačem „Já | Můj tým" (Prehled.jsx).
 */

const AKCENT = '#B47AEA';
const ZELENA = '#10B981';
const CERVENA = '#EF4444';
const ZLUTA = '#F59E0B';

const OBDOBI = [{ id: 'den', label: 'Dnes' }, { id: 'tyden', label: 'Týden' }, { id: 'mesic', label: 'Měsíc' }];
const DRUHY = [
    { id: 'smernice', label: 'Směrnice', kratce: 'S' },
    { id: 'skoleni', label: 'Školení', kratce: 'Š' },
    { id: 'certifikat', label: 'Certifikáty', kratce: 'C' },
    { id: 'dokument', label: 'Dokumenty', kratce: 'D' },
    { id: 'recept', label: 'Recepty', kratce: 'R' },
];
const NEPRITOMNOST = { dovolena: 'dovolená', nemoc: 'nemoc', osobni: 'osobní volno', jine: 'nepřítomnost' };
// Časová osa dne v řádku — pevně, ať jsou řádky pod sebou srovnané
const DEN_OD = 8;
const DEN_DO = 24;

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;
const cislo = (v, d = 0) => Number(v || 0).toLocaleString('cs-CZ', { maximumFractionDigits: d });
const minuty = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
const naHodiny = (cas) => { const [h, m] = String(cas || '0:0').split(':').map(Number); return h + (m || 0) / 60; };
const datumKratce = (d) => (d ? new Date(d).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }) : '');

/** Co dnes s člověkem je — krátký text a tón. */
function dnesStav(z) {
    const d = z.dnes;
    if (d.nepritomnost) return { text: `${NEPRITOMNOST[d.nepritomnost.typ] || 'nepřítomnost'} do ${datumKratce(d.nepritomnost.do)}`, ton: 'tichy', poradi: 5 };
    if (d.nepichl) return { text: `nepíchl (směna od ${d.smena.cas_od})`, ton: 'problem', poradi: 0 };
    if (d.v_praci) return { text: `v práci od ${d.prichod}${d.zpozdeni_min >= 5 ? ` · pozdě ${d.zpozdeni_min} min` : ''}`, ton: 'ok', poradi: 1 };
    if (d.smena && d.pichl) return { text: `odpracováno ${d.smena.cas_od}–${d.smena.cas_do}`, ton: 'tichy', poradi: 3 };
    if (d.smena) return { text: `směna ${d.smena.cas_od}–${d.smena.cas_do}`, ton: 'plan', poradi: 2 };
    return { text: 'volno', ton: 'tichy', poradi: 4 };
}

/** Seřazený seznam „Potřebuje tvou pozornost" napříč lidmi. */
function pozornostTymu(lide) {
    const polozky = [];
    lide.forEach(z => {
        if (z.dnes.nepichl) polozky.push({ id: `nepichl-${z.id}`, z, uroven: 0, titulek: 'Nepíchl příchod na směnu', popis: `směna ${z.dnes.smena.cas_od}–${z.dnes.smena.cas_do}`, akce: 'napsat' });
        z.povinnosti.filter(p => p.problem).forEach(p => polozky.push({ id: `${p.id}-${z.id}`, z, p, uroven: 0, titulek: p.titulek, popis: p.popis, akce: p.pripomenout ? 'pripomenout' : 'otevrit' }));
        if (z.obdobi.pozde >= 3) polozky.push({ id: `pozde-${z.id}`, z, uroven: 1, titulek: `${z.obdobi.pozde}× pozdní příchod`, popis: `celkem ${minuty(z.obdobi.pozde_min)}`, akce: 'napsat' });
        z.povinnosti.filter(p => !p.problem).forEach(p => polozky.push({ id: `${p.id}-${z.id}`, z, p, uroven: 2, titulek: p.titulek, popis: p.popis, akce: p.pripomenout ? 'pripomenout' : 'otevrit' }));
    });
    return polozky.sort((a, b) => a.uroven - b.uroven);
}

export default function MujTym({ user, isDark, isMobile, cardStyle, onNavigate }) {
    const [obdobi, setObdobi] = useState('mesic');
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [vykon, setVykon] = useState(null);
    const [vybranyId, setVybranyId] = useState(null);
    const [vsePozornost, setVsePozornost] = useState(false);
    const [pripomenuto, setPripomenuto] = useState({});   // klíč → 'bezi' | 'hotovo' | 'chyba'

    const barvy = useMemo(() => ({
        muted: isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)',
        tichy: isDark ? 'rgba(255,255,255,0.38)' : 'var(--text-muted)',
        cara: isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)',
        stopa: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
        vnitrni: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
        okraj: isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)',
        akcent: isDark ? AKCENT : '#831BE4',
        podklad: isDark ? '#141416' : '#fff',
    }), [isDark]);

    const nacti = useCallback(() => {
        setChyba(null);
        return fetch(`/api/me/tym?obdobi=${obdobi}`, { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
            .then(setData)
            .catch(() => setChyba('Tým se nepodařilo načíst.'));
    }, [obdobi]);
    useEffect(() => { nacti(); }, [nacti]);
    useEffect(() => {
        fetch('/api/me/tym/vykon', { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null)).then(setVykon).catch(() => setVykon(null));
    }, []);

    const lide = useMemo(() => [...(data?.lide || [])].sort((a, b) => dnesStav(a).poradi - dnesStav(b).poradi
        || naHodiny(a.dnes.smena?.cas_od) - naHodiny(b.dnes.smena?.cas_od)), [data]);
    const tym = data?.tym;
    const pozornost = useMemo(() => pozornostTymu(lide), [lide]);
    const vykonPodle = useMemo(() => Object.fromEntries((vykon?.rozvoz || []).map(r => [r.id, r])), [vykon]);
    const median = vykon?.rozvoz_median;
    const vybrany = lide.find(z => z.id === vybranyId) || null;

    // ── Akce ──
    const napsat = async (z) => {
        if (!z.uzivatel_id) return;
        try {
            const r = await fetch('/api/chat/konverzace', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ typ: 'soukromy', uzivatel_ids: [z.uzivatel_id] }),
            });
            const d = await r.json();
            if (r.ok && d.id) onNavigate?.('zpravy', { konverzaceId: d.id });
        } catch { /* zůstane, jak je */ }
    };
    const pripomenout = async (z, p) => {
        const klic = `${z.id}:${p.id}`;
        setPripomenuto(x => ({ ...x, [klic]: 'bezi' }));
        try {
            const r = await fetch(`/api/me/tym/${z.id}/pripomenout`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ druh: p.druh, cil_id: p.cil_id }),
            });
            setPripomenuto(x => ({ ...x, [klic]: r.ok ? 'hotovo' : 'chyba' }));
        } catch {
            setPripomenuto(x => ({ ...x, [klic]: 'chyba' }));
        }
    };

    // ── Stavební kousky ──
    const karta = (obsah, styl = null) => (
        <div className="rounded-2xl" style={{ ...cardStyle, padding: isMobile ? 14 : 18, minWidth: 0, ...styl }}>{obsah}</div>
    );
    const stitek = (text) => <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: barvy.muted }}>{text}</span>;
    const tlacitko = (label, onClick, { Icon = null, disabled = false, barva = barvy.akcent } = {}) => (
        <button type="button" onClick={onClick} disabled={disabled} className="inline-flex items-center justify-center"
            style={{ flexShrink: 0, gap: 6, height: isMobile ? 44 : 32, padding: '0 12px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
                background: 'transparent', color: barva, border: `1px solid ${barva}66`, opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap' }}>
            {Icon && <Icon size={14} />}{label}
        </button>
    );
    const tonBarva = (ton) => (ton === 'ok' ? ZELENA : ton === 'problem' ? CERVENA : ton === 'plan' ? 'var(--text-primary)' : barvy.muted);

    // ── Pruh čísel ──
    const cisla = () => {
        const sluzba = lide.filter(z => z.dnes.smena || z.dnes.v_praci).length;
        const vPraci = lide.filter(z => z.dnes.v_praci).length;
        const nepichli = lide.filter(z => z.dnes.nepichl).length;
        const hodiny = lide.reduce((n, z) => n + z.obdobi.hodiny, 0);
        const plan = lide.reduce((n, z) => n + z.obdobi.hodiny_plan, 0);
        const planDosud = lide.reduce((n, z) => n + z.obdobi.hodiny_plan_dosud, 0);
        const pozde = lide.reduce((n, z) => n + z.obdobi.pozde, 0);
        const pozdeMin = lide.reduce((n, z) => n + z.obdobi.pozde_min, 0);
        const rozdil = pozde - (tym?.minule?.pozde ?? 0);
        const vPoradku = lide.filter(z => !z.povinnosti.length).length;
        const poTerminu = lide.reduce((n, z) => n + z.povinnosti.filter(p => p.problem).length, 0);
        const velke = { fontSize: isMobile ? 24 : 28, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.1 };
        const male = { fontSize: 12.5, color: barvy.muted };
        const dlazdice = (popis, hodnota, pod, podBarva = null, navic = null) => karta(
            <div className="flex flex-col" style={{ gap: 6 }}>
                {stitek(popis)}
                <span style={velke}>{hodnota}</span>
                {navic}
                <span style={{ ...male, color: podBarva || barvy.muted }}>{pod}</span>
            </div>, { padding: isMobile ? 14 : '14px 18px' });

        return (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: isMobile ? 10 : 14, flexShrink: 0 }}>
                {dlazdice('Dnes ve službě', <>{sluzba} <span style={{ fontSize: 14, fontWeight: 500, color: barvy.muted }}>z {lide.length}</span></>,
                    nepichli ? `${nepichli} nepíchl${nepichli === 1 ? '' : 'o'} příchod` : `${vPraci} teď v práci`, nepichli ? CERVENA : vPraci ? ZELENA : null)}
                {dlazdice(`Odpracováno · ${tym?.obdobi?.nazev || ''}`, `${cislo(hodiny)} h`,
                    `z ${cislo(plan)} h plánu${planDosud > 0 ? ` · do dneška ${cislo(planDosud)} h` : ''}`, null,
                    <div style={{ position: 'relative', height: 6, borderRadius: 3, background: barvy.stopa }}>
                        <div style={{ width: `${plan ? Math.min(100, (hodiny / plan) * 100) : 0}%`, height: '100%', borderRadius: 3, background: AKCENT }} />
                        {plan > 0 && <div title="Plán do dneška" style={{ position: 'absolute', left: `${Math.min(100, (planDosud / plan) * 100)}%`, top: -4, width: 2, height: 14, background: 'var(--text-primary)' }} />}
                    </div>)}
                {dlazdice('Pozdní příchody', `${pozde}×`,
                    `${pozde ? `${minuty(pozdeMin)} · ` : ''}${rozdil === 0 ? 'stejně jako minule' : rozdil < 0 ? `o ${-rozdil} méně než minule` : `o ${rozdil} víc než minule`}`,
                    rozdil < 0 ? ZELENA : rozdil > 0 ? CERVENA : null)}
                {dlazdice('Povinnosti v pořádku', <>{vPoradku} <span style={{ fontSize: 14, fontWeight: 500, color: barvy.muted }}>z {lide.length}</span></>,
                    poTerminu ? `${poTerminu} po termínu nebo chybí` : 'nic po termínu', poTerminu ? CERVENA : ZELENA)}
            </div>
        );
    };

    // ── Jeden člověk: dnešek na časové ose ──
    const osaDne = (z, vyska) => {
        const s = z.dnes.smena;
        const ted = new Date();
        const tedH = ted.getHours() + ted.getMinutes() / 60;
        const pct = (h) => Math.max(0, Math.min(100, ((h - DEN_OD) / (DEN_DO - DEN_OD)) * 100));
        let pruh = null;
        if (s) {
            const zac = naHodiny(s.cas_od);
            const kon = naHodiny(s.cas_do) <= zac ? 24 : naHodiny(s.cas_do);
            const okraj = z.dnes.v_praci ? ZELENA : z.dnes.nepichl ? CERVENA : z.dnes.pichl ? barvy.tichy : barvy.muted;
            const vypln = z.dnes.v_praci ? `${ZELENA}8C` : z.dnes.nepichl ? `${CERVENA}59` : z.dnes.pichl ? barvy.stopa : 'transparent';
            const hotovo = z.dnes.pichl && !z.dnes.v_praci ? 100 : Math.max(0, Math.min(100, ((tedH - zac) / (kon - zac)) * 100));
            pruh = (
                <span style={{ position: 'absolute', top: 2, bottom: 2, left: `${pct(zac)}%`, width: `${pct(kon) - pct(zac)}%`, borderRadius: 4, boxSizing: 'border-box', border: `1.5px solid ${okraj}`, overflow: 'hidden' }}>
                    <span style={{ position: 'absolute', inset: 0, width: `${z.dnes.v_praci || z.dnes.nepichl || z.dnes.pichl ? hotovo : 0}%`, background: vypln }} />
                </span>
            );
        }
        return (
            <span style={{ position: 'relative', display: 'block', height: vyska, borderRadius: 5, background: barvy.vnitrni }}>
                {tedH >= DEN_OD && tedH <= DEN_DO && <span style={{ position: 'absolute', top: -2, bottom: -2, left: `${pct(tedH)}%`, width: 2, background: barvy.akcent, zIndex: 1 }} />}
                {pruh}
            </span>
        );
    };
    const tecky = (z) => (
        <span className="flex" style={{ gap: 4 }}>
            {DRUHY.map(d => {
                const polozky = z.povinnosti.filter(p => p.druh === d.id);
                const problem = polozky.some(p => p.problem);
                const [pozadi, barva] = !polozky.length ? [`${ZELENA}1F`, ZELENA] : problem ? [`${CERVENA}2E`, CERVENA] : [`${ZLUTA}33`, ZLUTA];
                return (
                    <span key={d.id} title={polozky.length ? polozky.map(p => p.titulek).join('\n') : `${d.label}: v pořádku`}
                        style={{ width: 22, height: 22, borderRadius: 11, display: 'inline-grid', placeItems: 'center', fontSize: 10.5, fontWeight: 800, background: pozadi, color: barva }}>
                        {d.kratce}
                    </span>
                );
            })}
        </span>
    );
    const vykonText = (z) => {
        const r = vykonPodle[z.id];
        if (!r || r.slib == null) return null;
        const lepsi = median != null ? r.slib >= median : null;
        return <span style={{ color: lepsi === null ? 'var(--text-primary)' : lepsi ? ZELENA : CERVENA }}>{cislo(r.slib, 1)} %</span>;
    };

    // ── Karta Lidé ──
    const SLOUPCE = 'minmax(180px, 1.1fr) minmax(220px, 1.6fr) 130px 110px 140px 90px 20px';
    const prepinac = (
        <div role="group" aria-label="Období" className="flex" style={{ gap: 2, padding: 3, borderRadius: 10, background: barvy.vnitrni, border: `1px solid ${barvy.cara}` }}>
            {OBDOBI.map(o => (
                <button key={o.id} type="button" aria-pressed={obdobi === o.id} onClick={() => setObdobi(o.id)}
                    style={{ height: isMobile ? 38 : 30, padding: isMobile ? '0 14px' : '0 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                        background: obdobi === o.id ? (isDark ? '#2A2A30' : '#fff') : 'transparent', color: obdobi === o.id ? 'var(--text-primary)' : barvy.muted,
                        boxShadow: obdobi === o.id && !isDark ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>
                    {o.label}
                </button>
            ))}
        </div>
    );

    const lideKarta = () => karta(<>
        <div className="flex items-center justify-between" style={{ gap: 12, marginBottom: isMobile ? 6 : 10, flexWrap: 'wrap', flexShrink: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>Lidé <span style={{ fontWeight: 500, color: barvy.muted }}>· {lide.length}</span></h2>
            {prepinac}
        </div>
        {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: SLOUPCE, gap: 16, padding: '8px 4px', borderBottom: `1px solid ${barvy.cara}`, flexShrink: 0,
                fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: barvy.tichy }}>
                <span>Člověk</span>
                <span className="flex justify-between"><span>Dnes</span><span style={{ fontWeight: 600, letterSpacing: 0 }}>{DEN_OD}–{DEN_DO} h</span></span>
                <span>Hodiny · {tym?.obdobi?.nazev || ''}</span>
                <span>Pozdě</span>
                <span title="Směrnice · Školení · Certifikáty · Dokumenty · Recepty">Povinnosti</span>
                <span style={{ textAlign: 'right' }}>Dovoz včas</span>
                <span />
            </div>
        )}
        <div style={isMobile ? null : { flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {lide.map(z => {
                const stav = dnesStav(z);
                const o = z.obdobi;
                const hodinyBar = (
                    <span style={{ display: 'block', height: 4, borderRadius: 2, background: barvy.stopa }}>
                        <span style={{ display: 'block', height: '100%', borderRadius: 2, width: `${o.hodiny_plan ? Math.min(100, (o.hodiny / o.hodiny_plan) * 100) : 0}%`, background: AKCENT }} />
                    </span>
                );
                if (isMobile) {
                    return (
                        <button key={z.id} type="button" onClick={() => setVybranyId(z.id)} className="w-full text-left flex flex-col"
                            style={{ gap: 8, padding: '12px 2px', background: 'none', border: 'none', borderBottom: `1px solid ${barvy.cara}`, cursor: 'pointer' }}>
                            <span className="flex items-center" style={{ gap: 10 }}>
                                <Avatar z={z} size={34} />
                                <span className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.cele_jmeno}</span>
                                    <span style={{ fontSize: 12.5, color: tonBarva(stav.ton) }}>{stav.text}</span>
                                </span>
                                <ChevronRight size={16} style={{ color: barvy.tichy, flexShrink: 0 }} />
                            </span>
                            {z.dnes.smena && osaDne(z, 12)}
                            <span className="flex items-center justify-between" style={{ gap: 8, fontSize: 12.5, color: barvy.muted }}>
                                <span>{cislo(o.hodiny)}/{cislo(o.hodiny_plan)} h · pozdě {o.pozde}×{vykonText(z) ? <> · dovoz {vykonText(z)}</> : null}</span>
                                {tecky(z)}
                            </span>
                        </button>
                    );
                }
                return (
                    <button key={z.id} type="button" onClick={() => setVybranyId(z.id)} className="w-full text-left"
                        style={{ display: 'grid', gridTemplateColumns: SLOUPCE, gap: 16, alignItems: 'center', minHeight: 58, padding: '6px 4px',
                            background: 'none', border: 'none', borderBottom: `1px solid ${barvy.cara}`, cursor: 'pointer' }}>
                        <span className="flex items-center" style={{ gap: 10, minWidth: 0 }}>
                            <Avatar z={z} size={34} />
                            <span className="flex flex-col" style={{ minWidth: 0 }}>
                                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.cele_jmeno}</span>
                                <span style={{ fontSize: 12, color: barvy.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[...new Set([z.pozice, ...(z.kariery || [])].filter(Boolean))].join(' · ') || '—'}</span>
                            </span>
                        </span>
                        <span className="flex flex-col" style={{ gap: 5, minWidth: 0 }}>
                            {osaDne(z, 14)}
                            <span style={{ fontSize: 12, color: tonBarva(stav.ton), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stav.text}</span>
                        </span>
                        <span className="flex flex-col" style={{ gap: 5 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{cislo(o.hodiny)} / {cislo(o.hodiny_plan)} h</span>
                            {hodinyBar}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: o.pozde >= 3 ? ZLUTA : o.pozde ? 'var(--text-primary)' : barvy.tichy, fontVariantNumeric: 'tabular-nums' }}>
                            {o.pozde ? `${o.pozde}× · ${o.pozde_min} min` : '—'}
                        </span>
                        {tecky(z)}
                        <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: barvy.tichy }}>{vykonText(z) || '—'}</span>
                        <ChevronRight size={16} style={{ color: barvy.tichy }} />
                    </button>
                );
            })}
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 11.5, color: barvy.tichy, flexShrink: 0 }}>
            Povinnosti: S směrnice · Š školení · C certifikáty · D dokumenty · R recepty{median != null ? ` · dovoz včas vůči mediánu týmu ${cislo(median, 1)} %` : ''}
        </p>
    </>, isMobile ? null : { display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 });

    // ── Potřebuje tvou pozornost ──
    const akcePolozky = (it) => {
        if (it.akce === 'napsat') return tlacitko('Napsat', () => napsat(it.z), { Icon: MessageCircle, disabled: !it.z.uzivatel_id });
        if (it.akce === 'pripomenout') {
            const st = pripomenuto[`${it.z.id}:${it.p.id}`];
            if (st === 'hotovo') return <span className="inline-flex items-center" style={{ flexShrink: 0, gap: 4, fontSize: 12.5, fontWeight: 600, color: ZELENA }}><Check size={14} /> Připomenuto</span>;
            return tlacitko(st === 'chyba' ? 'Znovu' : 'Připomenout', () => pripomenout(it.z, it.p), { Icon: Bell, disabled: st === 'bezi', barva: st === 'chyba' ? CERVENA : barvy.akcent });
        }
        return tlacitko('Otevřít', () => setVybranyId(it.z.id));
    };
    const pozornostKarta = () => {
        const ukazat = isMobile && !vsePozornost ? pozornost.slice(0, 4) : pozornost;
        const ton = [
            { pozadi: `${CERVENA}12`, okraj: `${CERVENA}47`, tecka: CERVENA },
            { pozadi: `${ZLUTA}10`, okraj: `${ZLUTA}40`, tecka: ZLUTA },
            { pozadi: barvy.vnitrni, okraj: barvy.cara, tecka: barvy.tichy },
        ];
        return karta(<>
            <div className="flex items-center justify-between" style={{ marginBottom: 12, flexShrink: 0 }}>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>Potřebuje tvou pozornost</h2>
                {pozornost.length > 0 && <span style={{ fontSize: 12.5, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: barvy.vnitrni, color: barvy.muted }}>{pozornost.length}</span>}
            </div>
            {pozornost.length === 0 && <p style={{ margin: 0, fontSize: 13.5, color: barvy.muted }}>Nic nehoří — všichni mají povinnosti v pořádku a nikdo nechybí.</p>}
            <div className="flex flex-col" style={{ gap: 8, ...(isMobile ? null : { flex: 1, minHeight: 0, overflowY: 'auto' }) }}>
                {ukazat.map(it => (
                    <div key={it.id} className="flex items-center" style={{ gap: 10, padding: '10px 12px', borderRadius: 12, background: ton[it.uroven].pozadi, border: `1px solid ${ton[it.uroven].okraj}` }}>
                        <span style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: ton[it.uroven].tecka }} />
                        <button type="button" onClick={() => setVybranyId(it.z.id)} className="flex flex-col text-left" style={{ flex: 1, minWidth: 0, gap: 2, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{it.titulek}</span>
                            <span style={{ fontSize: 12, color: barvy.muted }}>{it.z.cele_jmeno}{it.popis ? ` · ${it.popis}` : ''}</span>
                        </button>
                        {akcePolozky(it)}
                    </div>
                ))}
            </div>
            {isMobile && pozornost.length > ukazat.length && (
                <button type="button" onClick={() => setVsePozornost(true)} style={{ marginTop: 6, minHeight: 40, background: 'none', border: 'none', padding: 0, fontSize: 13.5, fontWeight: 600, color: barvy.akcent, cursor: 'pointer' }}>
                    Zobrazit další {pozornost.length - ukazat.length}
                </button>
            )}
        </>, isMobile ? null : { display: 'flex', flexDirection: 'column', minHeight: 0 });
    };

    // ── Celek ──
    const obsah = () => {
        if (chyba) return <p style={{ margin: 0, fontSize: 13.5, color: CERVENA }}>{chyba}</p>;
        if (data === null) return <p style={{ margin: 0, fontSize: 13.5, color: barvy.muted }}>Načítám tým…</p>;
        if (!lide.length) {
            return (
                <div className="flex flex-col items-center" style={{ gap: 8, padding: '48px 16px', color: barvy.muted, textAlign: 'center' }}>
                    <Users size={28} />
                    <span style={{ fontSize: 14 }}>Nemáš žádné podřízené.</span>
                </div>
            );
        }
        if (isMobile) {
            return (
                <div className="flex flex-col" style={{ gap: 14, paddingBottom: 16 }}>
                    {cisla()}{pozornostKarta()}{lideKarta()}
                </div>
            );
        }
        return (
            <div className="flex flex-col" style={{ gap: 14, height: '100%', minHeight: 0 }}>
                {cisla()}
                <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 400px)', gap: 14 }}>
                    <div className="flex flex-col" style={{ minHeight: 0 }}>{lideKarta()}</div>
                    {pozornostKarta()}
                </div>
            </div>
        );
    };

    return (
        <div style={isMobile ? { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {obsah()}
            {vybrany && (
                <ModalShell onClose={() => setVybranyId(null)} isDark={isDark} barva={AKCENT} sirka={620} ikona={Users} nadpis={vybrany.cele_jmeno}>
                    <DetailCloveka z={vybrany} obdobiNazev={tym?.obdobi?.nazev} isDark={isDark} isMobile={isMobile} barvy={barvy} cardStyle={cardStyle}
                        pripomenuto={pripomenuto} onPripomenout={(p) => pripomenout(vybrany, p)} onNapsat={() => napsat(vybrany)}
                        onDoLidi={smiNaStranku(user, 'zamestnanci') ? () => onNavigate?.('zamestnanci', { tab: 'seznam', zamestnanecId: vybrany.id }) : null} />
                </ModalShell>
            )}
        </div>
    );
}

/** Detail člověka v okně: dnešek, období, povinnosti s Připomenout, výkon. */
function DetailCloveka({ z, obdobiNazev, isDark, isMobile, barvy, cardStyle, pripomenuto, onPripomenout, onNapsat, onDoLidi }) {
    const o = z.obdobi;
    const stav = dnesStav(z);
    const akcePovinnosti = (p) => {
        if (!p.pripomenout) return null;
        const st = pripomenuto[`${z.id}:${p.id}`];
        if (st === 'hotovo') return <span className="inline-flex items-center gap-1" style={{ fontSize: 12, fontWeight: 600, color: ZELENA }}><Check size={13} /> Připomenuto</span>;
        return (
            <button type="button" onClick={() => onPripomenout(p)} disabled={st === 'bezi'} className="inline-flex items-center gap-1"
                style={{ minHeight: 32, fontSize: 12, fontWeight: 600, color: st === 'chyba' ? CERVENA : barvy.akcent, background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: st === 'bezi' ? 0.5 : 1 }}>
                <Bell size={13} /> {st === 'chyba' ? 'Nepovedlo se, zkusit znovu' : 'Připomenout'}
            </button>
        );
    };
    const sekce = (Icon, titulek) => (
        <p className="flex items-center" style={{ gap: 6, margin: '20px 0 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: barvy.muted }}>
            <Icon size={13} /> {titulek}
        </p>
    );
    const r = (l, h, barva = null) => (
        <div className="flex justify-between" style={{ minHeight: isMobile ? 40 : 32, alignItems: 'center', borderBottom: `1px solid ${barvy.cara}`, gap: 10, fontSize: 13.5 }}>
            <span style={{ color: barvy.muted }}>{l}</span>
            <b style={{ color: barva || 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{h}</b>
        </div>
    );
    const tl = { minHeight: 40, padding: '0 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${barvy.okraj}` };

    return (
        <div style={{ padding: isMobile ? '4px 4px 12px' : '4px 8px 12px' }}>
            <div className="flex items-center" style={{ gap: 14 }}>
                <Avatar z={z} size={48} />
                <div className="flex-1 min-w-0">
                    <p style={{ margin: 0, fontSize: 13, color: barvy.muted }}>{[...new Set([z.pozice, ...(z.kariery || [])].filter(Boolean))].join(' · ') || '—'}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 13.5, fontWeight: 600, color: stav.ton === 'ok' ? ZELENA : stav.ton === 'problem' ? CERVENA : 'var(--text-primary)' }}>{stav.text}</p>
                </div>
            </div>
            <div className="flex flex-wrap" style={{ gap: 8, marginTop: 14 }}>
                {z.uzivatel_id && <button type="button" onClick={onNapsat} className="inline-flex items-center gap-1.5" style={tl}><MessageCircle size={15} /> Napsat</button>}
                {onDoLidi && <button type="button" onClick={onDoLidi} className="inline-flex items-center gap-1.5" style={tl}><ExternalLink size={15} /> Otevřít v Lidech</button>}
            </div>

            {sekce(Clock, 'Dnes')}
            {r('Směna', z.dnes.smena ? `${z.dnes.smena.cas_od}–${z.dnes.smena.cas_do}${z.dnes.smena.nazev ? ` · ${z.dnes.smena.nazev}` : ''}` : (z.dnes.nepritomnost ? stav.text : 'volno'))}
            {r('Příchod', z.dnes.prichod ? `${z.dnes.prichod}${z.dnes.zpozdeni_min >= 5 ? ` · pozdě ${z.dnes.zpozdeni_min} min` : ''}` : (z.dnes.nepichl ? 'nepíchl' : '—'), z.dnes.nepichl ? CERVENA : null)}

            {sekce(CalendarDays, `Období · ${obdobiNazev || ''}`)}
            {r('Odpracováno', `${cislo(o.hodiny, 1)} z ${cislo(o.hodiny_plan, 1)} h`)}
            {r('Směny', `${o.smen} z ${o.smen_plan}`)}
            {r('Pozdní příchody', o.pozde ? `${o.pozde}× · ${minuty(o.pozde_min)}` : 'žádné')}

            {sekce(AlertTriangle, `Povinnosti${z.povinnosti.length ? ` · ${z.povinnosti.length}` : ''}`)}
            <KDoreseniSeznam polozky={z.povinnosti} isDark={isDark} prazdne="Všechno splněno — směrnice, školení, certifikáty i dokumenty." renderAkce={akcePovinnosti} />

            {sekce(BarChart3, 'Výkon tento měsíc')}
            <div style={{ height: isMobile ? 280 : 260, display: 'flex', flexDirection: 'column' }}>
                <MojeKpi isDark={isDark} cardStyle={cardStyle} obdobi="mesic" mobil={isMobile} zaklad={`/api/me/tym/${z.id}/kpi`} />
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: barvy.tichy }}>Srovnání je vůči mediánu celého týmu. U obsluhy jde o tržbu provozu za jeho směny, ne o osobní výkon.</p>
        </div>
    );
}
